import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { AppointmentCancellationReason, AppointmentStatus, Route, RouteParameters } from 'constants/'
import { ClickTrackingProvider, FormAnalyticsProvider, SessionDurationProvider, useAppointment, useKeyBindings } from 'hooks'
import { AppointmentCancellationFormData } from 'types'
import { hydratePathname } from 'util/'
import { Card } from '../../Card'
import { ImmutableAppointment, MutableAppointmentStatus, mutableAppointmentStatuses } from '../ImmutableAppointment'
import { appointmentToRedirectPathname } from '../util'
import { Form } from './Form'

const defaultValues: AppointmentCancellationFormData = {
  description: '',
  reason: '' as AppointmentCancellationReason,
}

const Cancellation = () => {
  const navigate = useNavigate()
  const { appointment, isLoading } = useAppointment()

  const redirectToReschedule = useCallback(() => {
    const pathname = hydratePathname(Route.APPOINTMENT_RESCHEDULE, {
      [RouteParameters.APPOINTMENT_ID]: appointment?.id as string,
      [RouteParameters.COUNTRY]: appointment?.country,
    })

    navigate(pathname)
  }, [appointment?.id, appointment?.country, navigate])

  useKeyBindings({
    'r e s': redirectToReschedule,
  })

  useEffect(() => {
    if (!appointment) return

    const redirectPathname = appointmentToRedirectPathname(appointment, [AppointmentStatus.CANCELED])

    if (redirectPathname) {
      navigate(redirectPathname)
    }
  }, [appointment])

  if (isLoading) {
    return null
  }

  const isMutableAppointment = mutableAppointmentStatuses.has(appointment?.status?.status as MutableAppointmentStatus)

  return isMutableAppointment ? (
    <SessionDurationProvider>
      <ClickTrackingProvider>
        <FormAnalyticsProvider<AppointmentCancellationFormData>>
          <Card>
            <Form defaultValues={defaultValues} />
          </Card>
        </FormAnalyticsProvider>
      </ClickTrackingProvider>
    </SessionDurationProvider>
  ) : (
    <ImmutableAppointment />
  )
}

export { Cancellation }
